import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Newspaper, Pause, Play, Plug, Plus } from "lucide-react";
import {
  type ChannelDetail,
  type ChannelSummary,
  PUBLISHER,
  byPublisher,
  channelState,
  problemKeys,
  useChannels,
  usePauseAll,
  useRemoveChannel,
  useUpdateChannel,
} from "@/lib/aggregator";
import { useCurrentUser } from "@/lib/auth";
import { formatMoment, formatNumber, formatRelative } from "@/lib/format";
import { AddChannelDialog } from "@/components/aggregator/AddChannelDialog";
import { ChannelDrawer } from "@/components/aggregator/ChannelDrawer";
import { ConnectionsDialog } from "@/components/aggregator/ConnectionsDialog";
import { errorText } from "@/components/aggregator/helpers";
import { HelpSection } from "@/components/HelpSection";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { StatusBadge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { cn } from "@/lib/cn";

/**
 * Aggregator: the company's own channels filled from other channels' posts —
 * read from their public pages, put through the channel's settings, published
 * by the company's bot. A channel that is switched on and has a source works
 * by itself; the page shows what each is doing and what holds it. Adding a
 * channel is the whole way in: the bot is made on that way if there is none.
 */
export function AggregatorPage() {
  const { t } = useTranslation();
  const { data: user } = useCurrentUser();
  // The help as it is for who publishes: the channel's account, or the bot.
  const help = PUBLISHER === "account" ? "aggregator.help_account" : "aggregator.help";
  const channels = useChannels();
  const update = useUpdateChannel();
  const remove = useRemoveChannel();
  const pauseAll = usePauseAll();

  const role = user?.impersonating ? "company_admin" : user?.role;
  const canEdit = role === "company_admin" || role === "company_operator" || role === "superadmin";
  const canManage = role === "company_admin" || role === "superadmin";

  const [adding, setAdding] = useState(false);
  const [connections, setConnections] = useState<"bots" | "ai" | null>(null);
  const [connectionsTab, setConnectionsTab] = useState<"bots" | "ai">("bots");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleting, setDeleting] = useState<{ id: string; title: string } | null>(null);
  const [stopping, setStopping] = useState(false);

  const data = useMemo(() => channels.data ?? [], [channels.data]);
  const switchedOn = data.filter((c) => c.status === "active").length;

  const openDetail = (id: string) => {
    setDetailId(id);
    setDrawerOpen(true);
  };
  const openConnections = (tab: "bots" | "ai") => {
    setConnectionsTab(tab);
    setConnections(tab);
  };

  const subtitle = useMemo(() => {
    if (data.length === 0) return t(byPublisher("aggregator.subtitle"));
    return t("aggregator.subtitle_counts", {
      total: data.length,
      working: data.filter((c) => channelState(c) === "working").length,
      posts: formatNumber(data.reduce((n, c) => n + c.published_24h, 0)),
    });
  }, [data, t]);

  const addButton = canEdit && (
    <Button
      onClick={() => setAdding(true)}
      iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
    >
      {t("aggregator.channel.add_open")}
    </Button>
  );

  return (
    <section className="mx-auto max-w-7xl space-y-6 px-6 py-8">
      <PageHeader
        title={t("aggregator.title")}
        subtitle={subtitle}
        actions={
          <>
            {/* The brake: with one channel its own switch is brake enough. */}
            {canEdit && switchedOn > 1 && (
              <Button
                variant="outline"
                onClick={() => setStopping(true)}
                iconLeft={<Pause size={16} strokeWidth={2} aria-hidden="true" />}
              >
                {t("aggregator.pause_all.open")}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => openConnections(PUBLISHER === "account" ? "ai" : "bots")}
              iconLeft={<Plug size={16} strokeWidth={2} aria-hidden="true" />}
            >
              {t(
                PUBLISHER === "account"
                  ? "aggregator.connections.open_account"
                  : "aggregator.connections.open",
              )}
            </Button>
            {addButton}
          </>
        }
      />

      {channels.isLoading && <p className="text-fg-muted">{t("aggregator.loading")}</p>}
      {channels.isError && <p className="text-danger">{t("aggregator.load_error")}</p>}

      {channels.data && data.length === 0 && (
        <div className="rounded-2xl border border-border bg-bg-secondary">
          <EmptyState
            icon={Newspaper}
            title={t("aggregator.empty_title")}
            description={t("aggregator.empty_hint")}
            action={addButton}
          />
          <ol className="mx-auto max-w-xl list-decimal space-y-1.5 px-10 pb-8 text-sm leading-relaxed text-fg-secondary">
            {[1, 2, 3, 4].map((n) => (
              <li key={n}>{t(byPublisher(`aggregator.setup.step${n}`))}</li>
            ))}
          </ol>
        </div>
      )}

      {data.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.map((channel) => (
            <ChannelCard
              key={channel.id}
              channel={channel}
              canEdit={canEdit}
              busy={update.isPending && update.variables?.id === channel.id}
              onOpen={() => openDetail(channel.id)}
              onToggle={() =>
                update.mutate(
                  { id: channel.id, status: channel.status === "active" ? "paused" : "active" },
                  {
                    onError: (err) =>
                      pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) }),
                  },
                )
              }
            />
          ))}
        </div>
      )}

      <HelpSection
        i18nKey={help}
        paragraphs={4}
        list={{
          titleKey: `${help}.list_title`,
          itemsKey: `${help}.list_items`,
          itemCount: 6,
        }}
      />

      <AddChannelDialog
        open={adding}
        onOpenChange={setAdding}
        onAdded={openDetail}
        canManage={canManage}
      />
      <ConnectionsDialog
        open={connections !== null}
        onOpenChange={(o) => !o && setConnections(null)}
        canManage={canManage}
        canEdit={canEdit}
        tab={connectionsTab}
        onTabChange={setConnectionsTab}
      />
      <ChannelDrawer
        id={detailId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        canEdit={canEdit}
        channels={data}
        onDelete={(channel: ChannelDetail) => setDeleting({ id: channel.id, title: channel.title })}
        onOpenConnections={openConnections}
      />
      <ConfirmDialog
        open={stopping}
        onOpenChange={setStopping}
        title={t("aggregator.pause_all.confirm", { n: formatNumber(switchedOn) })}
        description={t("aggregator.pause_all.hint")}
        confirmText={t("aggregator.pause_all.open")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          try {
            const out = await pauseAll.mutateAsync();
            pushToast({
              kind: "success",
              text: t("aggregator.pause_all.done", { n: formatNumber(out.paused) }),
            });
          } catch (err) {
            pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
          }
        }}
      />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        tone="danger"
        title={t("aggregator.channel.remove_confirm", { title: deleting?.title ?? "" })}
        description={t("aggregator.channel.remove_hint")}
        confirmText={t("aggregator.channel.remove")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            setDrawerOpen(false);
            pushToast({ kind: "success", text: t("aggregator.channel.removed") });
          } catch (err) {
            pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
          }
        }}
      />
    </section>
  );
}

function ChannelCard({
  channel,
  canEdit,
  busy,
  onOpen,
  onToggle,
}: {
  channel: ChannelSummary;
  canEdit: boolean;
  busy: boolean;
  onOpen: () => void;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const state = channelState(channel);
  const line =
    state === "stopped"
      ? t(problemKeys(channel.publisher, channel.problem), channel.problem ?? "")
      : state === "paused"
        ? // Added switched off and not yet switched on — not "paused" by anybody.
          t(channel.last_post_at === null ? "aggregator.card.new" : "aggregator.card.paused")
        : state === "idle"
          ? t("aggregator.card.idle")
          : channel.next_post_at
            ? t("aggregator.card.next", {
                when: formatMoment(channel.next_post_at, channel.timezone),
              })
            : channel.review > 0
              ? t("aggregator.card.waits_review")
              : channel.last_post_at
                ? t("aggregator.card.last", { when: formatRelative(channel.last_post_at) })
                : t("aggregator.card.waits_posts");

  const numbers: [string, number, string?][] = [
    [t("aggregator.card.published"), channel.published_24h],
    [t("aggregator.card.queued"), channel.queued + channel.pending],
    [t("aggregator.card.review"), channel.review, channel.review > 0 ? "text-warning" : undefined],
    [t("aggregator.card.failed"), channel.failed_24h, channel.failed_24h > 0 ? "text-danger" : undefined],
  ];

  return (
    <article
      className={cn(
        "flex flex-col rounded-2xl border border-border bg-bg-secondary transition-colors",
        "hover:border-fg-muted/50",
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        // A button centres what is in it: in a row of cards of different
        // heights the shorter one's title would sit lower than its neighbour's.
        className="flex flex-1 flex-col justify-start space-y-4 rounded-t-2xl px-5 pb-4 pt-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold leading-tight text-fg-primary">
              {channel.title}
            </h2>
            <p className="mt-0.5 truncate text-xs text-fg-muted">
              {channel.username ? `@${channel.username}` : t("aggregator.card.private")}
              {channel.members_count !== null &&
                ` · ${t("aggregator.card.members", { n: formatNumber(channel.members_count) })}`}
            </p>
          </div>
          <StatusBadge
            domain="aggregator"
            value={state}
            label={t(`aggregator.state.${state}`)}
            dot
          />
        </div>

        <div className="grid grid-cols-4 gap-2">
          {numbers.map(([label, value, tone]) => (
            <div key={label}>
              <div
                className={cn(
                  "font-mono text-xl font-semibold tabular-nums leading-none text-fg-primary",
                  tone,
                )}
              >
                {formatNumber(value)}
              </div>
              <div className="mt-1 text-[11px] leading-tight text-fg-muted">{label}</div>
            </div>
          ))}
        </div>

        <p className={cn("text-xs", state === "stopped" ? "text-danger" : "text-fg-muted")}>{line}</p>
        {channel.fill?.status === "running" && (
          <p className="text-xs text-fg-secondary">
            {channel.fill.total > 0
              ? t("aggregator.card.fill", {
                  published: formatNumber(channel.fill.published),
                  total: formatNumber(channel.fill.total),
                })
              : t("aggregator.card.fill_reading")}
          </p>
        )}
        {state !== "stopped" && channel.sources_broken > 0 && (
          <Alert tone="warning" icon={false} className="px-3 py-2 text-xs">
            {t("aggregator.overview.sources_broken", { count: channel.sources_broken })}
          </Alert>
        )}
      </button>

      <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3 text-xs text-fg-muted">
        <span className="truncate">
          {t("aggregator.card.sources", { count: channel.sources })} ·{" "}
          {t(`aggregator.ai.mode_${channel.ai_mode}`)}
          {channel.moderation === "all" && ` · ${t("aggregator.card.reviewed")}`}
        </span>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            loading={busy}
            onClick={onToggle}
            iconLeft={
              channel.status === "active" ? (
                <Pause size={14} strokeWidth={2} aria-hidden="true" />
              ) : (
                <Play size={14} strokeWidth={2} aria-hidden="true" />
              )
            }
          >
            {t(channel.status === "active" ? "aggregator.channel.pause" : "aggregator.channel.resume")}
          </Button>
        )}
      </div>
    </article>
  );
}
